<template>
  <BaseModal :value="true" @input="$emit('close')" id="return-modal">
    <CloseReturnModal
      v-if="showCloseReturnModal"
      :estimatedRefund="Number(returnData.estimatedRefundAmount)"
      :returnId="returnId"
      :currentStatus="returnData.status"
      :resolution="returnData.resolution"
      :returnShipmentPaidBy="returnData.shippingPayer"
      v-model="showCloseReturnModal"
      @updatedReturn="loadReturn()"
    ></CloseReturnModal>
    <EditReturnModal
      v-if="showEditReturnModal"
      :returnId="returnId"
      :returnShipmentPaidBy="returnData.shippingPayer"
      :resolution="returnData.resolution"
      :amountRefunded="returnData.amountRefunded"
      :estimatedRefund="Number(returnData.estimatedRefundAmount)"
      v-model="showEditReturnModal"
      @updatedReturn="loadReturn()"
    ></EditReturnModal>
    <template v-slot:header>
      <div v-if="returnData">RMA: {{ returnData.rma }}</div>
      <div v-if="returnData">Order number: {{ returnData.orderNumber }}</div>
    </template>
    <div id="print-area" class="p-10 relative" v-if="returnData">
      <button
        v-if="returnData"
        class="no-print absolute right-10 top-10 w-10 h-10"
        @click="print"
      >
        <img src="@/assets/icons/print.svg" class="w-full" />
      </button>
      <h1>Return</h1>
      <div v-if="loading">
        <BaseMiniSpinner />
      </div>
      <div v-else-if="returnData">
        <div class="flex flex-row my-2 gap-2">
          <button
            class="btn-dark-button"
            v-if="returnData.status !== 'Completed'"
            @click="openCloseReturnModal"
          >
            Close return
          </button>
          <button class="btn-dark-button" @click="OpenEditModal">
            Edit return
          </button>
          <button
            v-if="returnData.status === 'Requested'"
            @click="markInTransit"
            class="btn-info-button"
            :disabled="markingInTransit"
          >
            {{ markingInTransit ? "Marking..." : "Mark as In Transit" }}
          </button>
          <button
            v-if="returnData.status === 'In transit'"
            @click="showReceiveModal = true"
            class="btn-dark-button"
          >
            {{ $t("message.returns.action_receive") }}
          </button>
          <button
            v-if="returnData.status === 'receivedInWarehouse'"
            @click="showRefundModal = true"
            class="btn-success-button"
            :class="{
              'opacity-50 cursor-not-allowed':
                (returnData.totalRefundableAmount || 0) <= 0
            }"
            :disabled="(returnData.totalRefundableAmount || 0) <= 0"
          >
            {{ $t("message.returns.action_refund") }} (${{
              returnData.totalRefundableAmount || 0
            }})
          </button>
        </div>
        <div class="flex flex-row gap-6">
          <div id="general-data" class="flex-1">
            <h2>RMA: {{ returnData.rma }}</h2>
            <h3>
              Status:
              <span class="text-red-500 font-bold">{{
                returnData.status
              }}</span>
            </h3>
            <p>
              Resolution:
              <span>{{ returnData.resolution }}</span>
            </p>
            <p>
              Order number: {{ returnData.orderNumber }}
              <router-link
                :to="{
                  name: 'OrdersPage',
                  params: urlParams,
                  query: { orderOpen: returnData.orderNumber }
                }"
                target="_blank"
                class="text-blue-500"
              >
                Open Order
              </router-link>
            </p>
            <p>
              <a :href="orderWebURL" target="_blank" class="text-blue-500">
                Open Order User view
              </a>
            </p>
            <p>
              <a
                :href="orderReturnWebURL"
                target="_blank"
                class="text-blue-500"
              >
                Open return User view
              </a>
            </p>
            <p>Name: {{ returnData.customerName }}</p>
            <p>Return date: {{ formatDateWithHour(returnData.date) }}</p>
            <p>Contact email: {{ returnData.customerEmail }}</p>
            <p>Contact phone: {{ returnData.customerPhone }}</p>
            <p>Requested solution: {{ returnData.resolution }}</p>
            <p>Estimated refund: ${{ returnData.estimatedRefundAmount }}</p>
            <p class="font-bold text-lg">
              Amount refunded: <b>${{ returnData.amountRefunded }}</b>
            </p>
            <p class="font-bold text-lg">
              Available for refund:
              <b class="text-green-600"
                >${{ returnData.totalRefundableAmount || 0 }}</b
              >
            </p>
            <p>SKUs returned: {{ returnData.itemsQty }}</p>
            <p>
              Shipping covered by:
              <span v-if="returnData.shippingPayer !== ''">{{
                returnData.shippingPayer
              }}</span
              ><span v-else>Not defined</span>
            </p>
            <p class="bg-gray-100 p-2 border rounded-md my-2">
              Customer details: {{ returnData.details }}
            </p>
          </div>
          <div id="refunds-list" class="flex-1">
            <ReturnStripePayments
              v-if="isStripeEnabled && canRefundPayment"
              :returnId="returnId"
              :orderNumber="returnData.orderNumber"
              :returnRefundable="Number(returnData.totalRefundableAmount || 0)"
              @refunded="loadReturn()"
            />
            <h2>Refunds History</h2>
            <div
              v-if="returnData.refunds && returnData.refunds.length > 0"
              class="space-y-3"
            >
              <div
                v-for="refund in returnData.refunds"
                :key="refund.id"
                class="bg-gray-50 border border-gray-200 rounded-lg p-4"
              >
                <div class="flex justify-between items-start mb-2">
                  <h3 class="font-semibold text-lg text-green-600">
                    ${{ refund.refundAmount }}
                  </h3>
                  <span
                    class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded"
                  >
                    {{ refund.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-600 mb-1">
                  <strong>Method:</strong> {{ refund.refundMethod }}
                </p>
                <p class="text-sm text-gray-600 mb-1">
                  <strong>Date:</strong> {{ formatDateWithHour(refund.date) }}
                </p>
                <p class="text-xs text-gray-500">ID: {{ refund.id }}</p>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <div
                class="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  class="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  ></path>
                </svg>
              </div>
              <h3 class="text-gray-600 font-medium mb-1">No refunds yet</h3>
              <p class="text-gray-400 text-sm">
                Refunds will appear here once processed
              </p>
            </div>
          </div>
        </div>
        <hr />
        <div id="products">
          <h2>Products on return</h2>
          <div id="products-content">
            <div
              v-for="product in returnData.products"
              :key="product.id"
              class="flex flex-row gap-2 border-b border-black py-2"
            >
              <div class="w-20">
                <img
                  v-if="product.image && product.image !== ''"
                  :src="product.image"
                  class="w-full"
                />
                <div v-else class="w-20 h-20 flex justify-center items-center">
                  No Image
                </div>
              </div>
              <div class="flex-grow flex flex-col">
                <p>{{ product.sku }}</p>
                <p>
                  {{ product.description }}
                </p>
                <!-- Product Condition Alert -->
                <div
                  v-if="product.condition"
                  class="mt-2 mb-2 px-3 py-2 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-md text-sm"
                >
                  <div class="flex items-center gap-2">
                    <span class="font-semibold">⚠️ Condition:</span>
                    <span>{{ product.condition }}</span>
                  </div>
                </div>
                <p>Quantity: {{ product.qty }}</p>
                <p v-if="product.receivedQty">
                  <strong>Received: {{ product.receivedQty }}</strong>
                  <span class="text-sm text-gray-600 ml-2">
                    by {{ product.receivedByName || "Unknown" }} on
                    {{ formatDateWithHour(product.receivedDate) }}
                  </span>
                </p>
                <p v-else class="text-gray-500">Not received yet</p>
                <p>Amount: ${{ product.unitPrice }}</p>
                <p>Return reason: {{ product.returnReason }}</p>
                <p v-if="product.comment != ''">
                  Comment: {{ product.comment }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div id="return-images">
          <h2>Prove Images</h2>
          <p>Add prove images if needed</p>
          <div class="my-4 flex flex-row flex-wrap gap-2">
            <a
              v-for="(image, index) in returnData.images"
              :key="index"
              :href="image.imageUrl"
              target="_blank"
              class="w-20 h-20 flex justify-center items-center"
            >
              <img :src="image.imageUrl" class="w-full" />
            </a>
            <BaseImageUploader @imageUploaded="imageUploaded" />
          </div>
        </div>
        <hr class="no-print" />
        <div id="labels">
          <h2>Return Labels</h2>
          <div
            v-if="returnData.labels && returnData.labels?.length > 0"
            class="mt-4 flex flex-row flex-wrap gap-2"
          >
            <div
              v-for="(label, index) in returnData.labels"
              :key="index"
              class="flex flex-col border rounded-md p-2"
            >
              <span
                v-if="label.status === 'voided'"
                class="text-xs bg-red-100 text-red-700 px-2 py-1 rounded mb-2 self-start font-semibold"
                >Anulada</span
              >
              <span
                v-else-if="label.status === 'used'"
                class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded mb-2 self-start font-semibold"
                >Usada</span
              >
              <span
                v-else-if="label.status === 'legacy'"
                class="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded mb-2 self-start font-semibold"
                >Legacy</span
              >
              <a
                :href="label.labelUrl"
                target="_blank"
                class="btn-dark-button mb-4"
                >Download Label</a
              >
              <p>carrier: {{ label.carrier }}</p>
              <p>Service: {{ label.service }}</p>
              <p>
                tracking:
                <a
                  class="text-red-500"
                  :href="
                    `https://www.ups.com/track?loc=en_US&tracknum=${label.tracking}&requester=ST/`
                  "
                  target="_blank"
                >
                  {{ label.tracking }}
                </a>
              </p>
              <p>Price: ${{ label.cost }}</p>
              <p>Creation Date: {{ label.date }}</p>
              <button
                class="btn-danger-button no-print mt-3"
                :disabled="expiringLabelId === label.id"
                @click="expireLabel(label)"
              >
                {{
                  expiringLabelId === label.id
                    ? "Procesando..."
                    : "Verificar y eliminar"
                }}
              </button>
            </div>
          </div>
          <p v-else>
            Labels not provided yet
          </p>
          <button
            v-if="!creatingLabel"
            class="btn-info-button my-2"
            @click="shopLabelCreationModal = true"
          >
            Request label
          </button>
          <ReturnLabelCreationModal
            v-model="shopLabelCreationModal"
            :labels="returnData.labels"
            :returnId="returnId"
            :originalAddress="returnData.address"
            @labelCreated="createdLabel"
          />
        </div>
        <div id="return-actions-modals-wrapper">
          <ReturnActionsModals
            v-if="returnData"
            :returnId="returnId"
            :returnData="returnData"
            :show-receive-modal.sync="showReceiveModal"
            :show-refund-modal.sync="showRefundModal"
            @returnUpdated="loadReturn()"
          />
        </div>
        <hr class="no-print" />
        <div id="comments" class="no-print">
          <h2>More comments</h2>
          <div
            v-for="comment in returnData.comments"
            :key="comment.id"
            class="my-1 bg-gray-100 p-2 rounded-md border-gray-400 flex flex-col"
          >
            <p>
              {{ comment.comment }}
            </p>
            <p class="text-xs mt-2">
              {{ comment.name || "guest" }} at
              {{ formatDateWithHour(comment.date) }}
            </p>
          </div>
          <BaseForm
            class="no-print bg-gray-200 p-2 rounded-md"
            @submit="sendComment"
          >
            <BaseTextField v-model="newComment" label="Add a comment" />
            <button
              class="btn-dark-button w-40 flex justify-center items-center"
            >
              <BaseMiniSpinner v-if="sendingComment" />
              <span v-else>
                Send comment
              </span>
            </button>
          </BaseForm>
        </div>
      </div>
      <div v-else>
        Not found
      </div>
    </div>
    <base-mini-spinner v-else></base-mini-spinner>
  </BaseModal>
</template>
<script>
import { formatDateWithHour } from "@/helpers/globalHelpers.js";
import CloseReturnModal from "@/components/order/CloseReturnModal.vue";
import EditReturnModal from "@/components/order/EditReturnModal.vue";
import ReturnLabelCreationModal from "@/components/order/ReturnLabelCreationModal.vue";
import ReturnActionsModals from "@/components/order/ReturnActionsModals.vue";
import ReturnStripePayments from "@/components/order/ReturnStripePayments.vue";
import {
  getReturn as getReturnApi,
  addImageToReturn,
  addReturnComment,
  markReturnInTransit,
  expireReturnLabel
} from "@/api/store/ReturnsService.js";
export default {
  components: {
    CloseReturnModal,
    EditReturnModal,
    ReturnLabelCreationModal,
    ReturnActionsModals,
    ReturnStripePayments
  },
  props: {
    returnId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      returnData: null,
      loading: true,
      newComment: "",
      sendingComment: false,
      showCloseReturnModal: false,
      showEditReturnModal: false,
      creatingLabel: false,
      shopLabelCreationModal: false,
      markingInTransit: false,
      showReceiveModal: false,
      showRefundModal: false,
      expiringLabelId: null
    };
  },
  computed: {
    canRefundPayment() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    },
    isStripeEnabled() {
      return (
        this.$store.getters["general/getAdminSettings"]?.get("payment_stripe")
          ?.value === "true"
      );
    },
    urlParams() {
      return {
        storeCode: this.activeStore,
        departmentCode: this.activeDepartment
      };
    },
    activeDepartment() {
      return this.$store.getters["general/getActiveDepartment"]?.code;
    },
    activeStore() {
      return this.$store.getters["general/getActiveStore"]?.code;
    },
    orderWebURL() {
      return ` ${this.$store.getters["general/getActiveStore"].url}order/${this.returnData.orderNumber}-${this.returnData.orderValidator}`;
    },
    orderReturnWebURL() {
      return ` ${this.$store.getters["general/getActiveStore"].url}order/return/${this.returnData.id}`;
    }
  },
  mounted() {
    this.loadReturn();
  },
  methods: {
    async sendComment() {
      if (this.newComment == "") return;

      this.sendingComment = true;

      try {
        const commentCreated = await addReturnComment(
          this.returnId,
          this.newComment
        );
        console.log(commentCreated);
        this.returnData.comments.push(commentCreated);
        this.newComment = "";
      } catch (error) {
        const message =
          error.response?.data?.error?.message || "Comment could not be sent";
        this.$toast.open({
          message: message,
          type: "error",
          position: "top-center"
        });
      }
      this.sendingComment = false;
    },
    async loadReturn() {
      this.returnData = null;
      this.returnData = await getReturnApi(this.returnId);
      this.loading = false;
      this.$emit("returnUpdated");
    },
    print() {
      window.print();
    },
    formatDateWithHour,
    async imageUploaded(img) {
      if (img == "") return;

      try {
        const imageUploaded = await addImageToReturn(this.returnId, img);
        this.returnData.images.push(imageUploaded);
        this.$toast.open({
          message: "Image uploaded",
          type: "success",
          position: "top-right"
        });
      } catch (error) {
        const message =
          error.response?.data?.error?.message || "Image could not be sent";
        this.$toast.open({
          message: message,
          type: "error",
          position: "top-right"
        });
      }
    },
    openCloseReturnModal() {
      this.showCloseReturnModal = true;
    },
    OpenEditModal() {
      this.showEditReturnModal = true;
    },
    createdLabel(label) {
      this.returnData.labels.push(label);
      this.shopLabelCreationModal = false;
    },
    async expireLabel(label) {
      const confirmed = window.confirm(
        `Esto va a anular la label ${label.tracking} en ShipStation y borrar el PDF de S3 si no fue usada. ¿Continuar?`
      );
      if (!confirmed) return;

      this.expiringLabelId = label.id;
      try {
        const result = await expireReturnLabel(label.id);
        const detail = result.detail || result.status;
        let toastType = "warning";
        if (result.status === "voided" || result.s3Deleted) {
          toastType = "success";
        } else if (result.status === "legacy" && !result.s3Deleted) {
          toastType = "error";
        }
        this.$toast.open({
          message: `Label ${result.status}: ${detail}`,
          type: toastType,
          position: "top-right"
        });
        this.loadReturn();
      } catch (error) {
        const message =
          error.response?.data?.error?.message || "Error al procesar la label";
        this.$toast.open({
          message: message,
          type: "error",
          position: "top-right"
        });
      } finally {
        this.expiringLabelId = null;
      }
    },
    async markInTransit() {
      this.markingInTransit = true;
      try {
        await markReturnInTransit(this.returnId);

        this.$toast.open({
          message: "Return marked as In Transit",
          type: "success",
          position: "top-right"
        });

        this.loadReturn();
      } catch (error) {
        this.$toast.open({
          message: error.message || "Error marking return as in transit",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.markingInTransit = false;
      }
    }
  }
};
</script>
<style lang="postcss">
#return-modal {
  &.modal {
    padding-top: 40px;
    .modal-content {
      height: 95%;
      width: 80%;
      max-width: 1560px;
      @media screen and (max-width: 1450px) {
        width: 99%;
        height: 99%;
      }
    }
    @media screen and (max-width: 1450px) {
      padding-top: 5px;
    }
  }
}
@media print {
  header .flex,
  .sidenav-container,
  footer {
    display: none !important;
  }
  #return-images,
  #labels,
  .no-print {
    display: none !important;
  }
  .modal-content {
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}
/* Ensure ReturnActionsModals component stays small */
#return-actions-modals-wrapper ::v-deep .modal {
  .modal-content {
    max-width: 500px !important;
    width: 90% !important;
  }
}
</style>
